import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Share2, Save, CheckCircle2, Gift, Sparkles, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

/**
 * RegistrationWall — shown when an anonymous visitor tries to Download/Share/Save an invoice.
 * Value-first: lists the benefits, gives Continue-with-Google as the primary path,
 * Continue-with-Email as secondary. Draft + pending intent are already in localStorage
 * so the /auth flow (or the Google OAuth redirect) can resume seamlessly.
 */
const INTENT_META = {
  save: { icon: Save, title: "Save your invoice", verb: "save this invoice" },
  download: { icon: Download, title: "Download your invoice", verb: "download the PDF" },
  share: { icon: Share2, title: "Share your invoice", verb: "share this invoice" },
  save_master: { icon: Save, title: "Save to your master", verb: "save this to your customer/product master" },
};

export default function RegistrationWall({ open, onClose, intent = "save", customerName = "" }) {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const [busy, setBusy] = useState(false);
  const meta = INTENT_META[intent] || INTENT_META.save;
  const Icon = meta.icon;

  if (!open) return null;

  const handleGoogle = async () => {
    setBusy(true);
    try { await loginWithGoogle(); } catch { setBusy(false); }
    // OAuth performs a redirect — the invoice-builder page will resume on return via localStorage.
  };

  const handleEmail = () => {
    navigate("/auth?resume=1");
  };

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
        onClick={onClose}
        data-testid="registration-wall"
      >
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            data-testid="wall-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Header with celebration */}
          <div className="bg-gradient-to-br from-emerald-50 via-white to-blue-50 px-6 pt-6 pb-4 text-center">
            <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-700 mb-1">
              Your invoice is ready
            </p>
            <h3 className="text-xl font-black text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Create your free account to {meta.verb}
            </h3>
            {customerName && (
              <p className="text-xs text-slate-500 mt-1">
                For <span className="font-semibold text-slate-700">{customerName}</span>
              </p>
            )}
          </div>

          {/* Value bullets */}
          <div className="px-6 py-4">
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span><Icon className="inline w-3.5 h-3.5 mr-0.5 text-slate-500" /> {meta.title}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span>Save unlimited invoices &amp; reuse customers/products</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span>GSTR-1 / 3B / HSN reports auto-generated</span>
              </li>
              <li className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-2 py-1.5">
                <Gift className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <span className="text-yellow-900"><span className="font-bold">30 days of Growth Plan free</span> — no card required</span>
              </li>
            </ul>
          </div>

          {/* CTAs */}
          <div className="px-6 pb-6 pt-2 space-y-2.5">
            <Button
              onClick={handleGoogle}
              disabled={busy}
              className="w-full bg-white border-2 border-slate-200 text-slate-800 font-semibold hover:bg-slate-50 hover:border-slate-300"
              data-testid="wall-google-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              )}
              Continue with Google
            </Button>
            <Button
              onClick={handleEmail}
              disabled={busy}
              variant="outline"
              className="w-full"
              data-testid="wall-email-btn"
            >
              Continue with Email
            </Button>
            <p className="text-[11px] text-center text-slate-400 pt-1">
              Your invoice is safely saved in your browser. We'll finish {meta.verb} right after you sign in.
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
